/**
 * Prioris · Team & RBAC components.
 *
 * Exports:
 *   - PriorisTeamPage         : org chart + team provisioning table (Settings→Team)
 *   - PriorisFirstLoginGate   : intercepts users with `must_change_password`
 *                                forces a password change, then shows the
 *                                role welcome screen.
 *
 * All data/mutations go via /api/prioris/rbac/*.
 */
import { useEffect, useMemo, useState } from 'react';
import { fetchSln } from '../nexus/nexusHelpers';
import {
  Users, UserPlus, Shield, AlertTriangle, CheckCircle2, Copy, X, Loader2,
  Mail, RotateCw, Ban, Trash2, Info, Lock, ChevronRight,
} from 'lucide-react';

const RBAC = '/api/prioris/rbac';

const STATUS_STYLE = {
  active:      { bg: 'rgba(34,197,94,0.15)',  color: '#22c55e', label: 'Active' },
  pending:     { bg: 'rgba(234,179,8,0.15)',  color: '#eab308', label: 'Invitation sent' },
  deactivated: { bg: 'rgba(148,163,184,0.15)',color: '#94a3b8', label: 'Deactivated' },
};

/* ─────────────────────────────────────────────────────────────── */
export function PriorisTeamPage({ t, project }) {
  const [state, setState] = useState({ loading: true, roles: [], memberships: [], fill: {}, is_project_admin: false });
  const [drafts, setDrafts] = useState({});     // key "role#seat" -> { email, full_name }
  const [busy, setBusy] = useState({});         // per-row busy flag
  const [credsModal, setCredsModal] = useState(null); // { email, temp_password, email_delivery, role_label }
  const [banner, setBanner] = useState(null);

  const load = async () => {
    if (!project?.id) return;
    setState(s => ({ ...s, loading: true }));
    try {
      const r = await fetchSln(`${RBAC}/team?project_id=${project.id}`);
      setState({ loading: false, ...r });
    } catch (e) {
      setBanner({ kind: 'error', msg: e.message });
      setState(s => ({ ...s, loading: false }));
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [project?.id]);

  const setDraft = (key, patch) => setDrafts(d => ({ ...d, [key]: { ...(d[key] || {}), ...patch } }));

  const doInvite = async (role, key) => {
    const d = drafts[key] || {};
    if (!d.email || !/^\S+@\S+\.\S+$/.test(d.email)) { setBanner({ kind: 'error', msg: 'Enter a valid email address.' }); return; }
    setBusy(b => ({ ...b, [key]: true }));
    try {
      const r = await fetchSln(`${RBAC}/invite`, {
        method: 'POST',
        body: JSON.stringify({ project_id: project.id, role, email: d.email.trim(), full_name: (d.full_name || '').trim() }),
      });
      setCredsModal({ email: d.email.trim(), temp_password: r.temp_password, email_delivery: r.email_delivery, role_label: state.roles.find(x => x.key === role)?.label });
      setDrafts(x => ({ ...x, [key]: {} }));
      await load();
    } catch (e) { setBanner({ kind: 'error', msg: e.message }); }
    setBusy(b => ({ ...b, [key]: false }));
  };

  const doResend = async (mbId) => {
    setBusy(b => ({ ...b, [mbId]: true }));
    try {
      const r = await fetchSln(`${RBAC}/memberships/${mbId}/resend`, { method: 'POST' });
      const mb = state.memberships.find(m => m.id === mbId);
      setCredsModal({ email: mb?.email || '', temp_password: r.temp_password, email_delivery: r.email_delivery, role_label: state.roles.find(x => x.key === mb?.role)?.label });
      await load();
    } catch (e) { setBanner({ kind: 'error', msg: e.message }); }
    setBusy(b => ({ ...b, [mbId]: false }));
  };

  const doPatch = async (mbId, patch) => {
    setBusy(b => ({ ...b, [mbId]: true }));
    try {
      await fetchSln(`${RBAC}/memberships/${mbId}`, { method: 'PATCH', body: JSON.stringify(patch) });
      await load();
    } catch (e) { setBanner({ kind: 'error', msg: e.message }); }
    setBusy(b => ({ ...b, [mbId]: false }));
  };

  // Group memberships by role + generate virtual rows up to max_seats
  const byRole = useMemo(() => {
    const map = {};
    for (const r of state.roles || []) map[r.key] = [];
    for (const m of state.memberships || []) { (map[m.role] || (map[m.role] = [])).push(m); }
    // Sort each by status (active first) then invited date
    for (const k of Object.keys(map)) {
      map[k].sort((a, b) => (a.status === 'active' ? 0 : 1) - (b.status === 'active' ? 0 : 1));
    }
    return map;
  }, [state.memberships, state.roles]);

  if (state.loading) {
    return <div className="p-6 text-[11px]" style={{ color: t.textDim }}>Loading team…</div>;
  }

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="prioris-team-page">
      <div className="mb-5 flex items-start justify-between flex-wrap gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: t.accent }}>Settings · Team</div>
          <h2 className="text-lg font-bold" style={{ color: t.text }}>{project?.asset_name || 'Project Team'}</h2>
          <p className="text-[11px] mt-1" style={{ color: t.textDim }}>
            Assign users to the seven project roles. Seat limits are enforced. Invited users receive an onboarding email and must set a new password on first login.
          </p>
        </div>
        {state.is_platform_creator && (
          <button
            onClick={async () => {
              try {
                const r = await fetchSln(`${RBAC}/test-invite`, { method: 'POST' });
                const d = r.email_delivery || {};
                if (d.status === 'sent') {
                  setBanner({ kind: 'ok', msg: `Preview email sent to ${r.to} (id ${d.email_id?.slice(0, 8)}…).` });
                } else if (d.status === 'skipped_no_key') {
                  setBanner({ kind: 'error', msg: 'Resend not configured — check RESEND_API_KEY in backend/.env.' });
                } else {
                  setBanner({ kind: 'error', msg: `Resend error: ${d.detail || 'unknown'}. Temp pw: ${r.temp_password}` });
                }
              } catch (e) { setBanner({ kind: 'error', msg: e.message }); }
            }}
            className="px-2.5 py-1 rounded text-[11px] font-semibold flex items-center gap-1"
            style={{ background: 'rgba(99,102,241,0.15)', color: '#818cf8', border: '1px solid rgba(99,102,241,0.35)' }}
            data-testid="team-test-invite-btn"
            title="Send a preview onboarding email to yourself">
            <Mail className="w-3 h-3" /> Try a test invite
          </button>
        )}
      </div>

      {banner && (
        <div className="mb-3 px-3 py-2 rounded text-[11px] flex items-start gap-2"
          style={{ background: banner.kind === 'error' ? 'rgba(239,68,68,0.1)' : 'rgba(34,197,94,0.1)', border: `1px solid ${banner.kind === 'error' ? 'rgba(239,68,68,0.35)' : 'rgba(34,197,94,0.35)'}` }}>
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5" style={{ color: banner.kind === 'error' ? '#ef4444' : '#22c55e' }} />
          <div style={{ color: t.text, flex: 1 }}>{banner.msg}</div>
          <button onClick={() => setBanner(null)}><X className="w-3 h-3" style={{ color: t.textDim }} /></button>
        </div>
      )}

      {/* Role-of-viewer ribbon */}
      {(state.is_platform_creator || state.is_project_admin) && (
        <div className="mb-3 px-3 py-2 rounded text-[11px] flex items-center gap-2"
          style={{ background: state.is_platform_creator ? 'rgba(99,102,241,0.12)' : 'rgba(16,185,129,0.12)',
                   border: `1px solid ${state.is_platform_creator ? 'rgba(99,102,241,0.35)' : 'rgba(16,185,129,0.35)'}` }}
          data-testid="team-viewer-banner">
          <Shield className="w-3.5 h-3.5 flex-shrink-0" style={{ color: state.is_platform_creator ? '#818cf8' : '#10b981' }} />
          <div style={{ color: t.text }}>
            {state.is_platform_creator ? (
              <><strong>You're the Platform Creator</strong> — you can invite any role (including Project Admin) and manage this project.</>
            ) : (
              <><strong>You're the Project Admin</strong> — you can invite the rest of the team. Only the Platform Creator can provision another Project Admin.</>
            )}
          </div>
        </div>
      )}

      {/* Org chart */}
      <OrgChart t={t} roles={state.roles || []} fill={state.fill || {}} />

      {/* Per-role provisioning blocks */}
      <div className="mt-6 space-y-5">
        {(state.roles || []).map(r => {
          const members = byRole[r.key] || [];
          const usedActive = members.filter(m => m.status !== 'deactivated').length;
          const below = usedActive < r.min_seats;
          return (
            <div key={r.key} className="rounded-lg overflow-hidden" style={{ border: `1px solid ${t.cardBorder}` }} data-testid={`team-role-${r.key}`}>
              <div className="px-3 py-2 flex items-baseline justify-between flex-wrap gap-2" style={{ background: t.inputBg }}>
                <div>
                  <div className="text-[12px] font-bold" style={{ color: t.text }}>{r.label}</div>
                  <div className="text-[10px]" style={{ color: t.textDim }}>{r.summary}</div>
                </div>
                <div className="flex items-center gap-2 text-[10px]">
                  <span className="px-1.5 py-0.5 rounded font-semibold"
                    style={{ background: below ? 'rgba(234,179,8,0.15)' : 'rgba(34,197,94,0.15)', color: below ? '#eab308' : '#22c55e' }}
                    data-testid={`team-fill-${r.key}`}>
                    {usedActive} / {r.max_seats} assigned
                  </span>
                  {below && <span style={{ color: '#eab308' }}>min {r.min_seats} required</span>}
                </div>
              </div>

              <table className="w-full text-[11px]" style={{ borderCollapse: 'separate', borderSpacing: 0, background: t.card }}>
                <thead>
                  <tr style={{ color: t.textDim }}>
                    {['Seat', 'Email', 'Full name', 'Status', 'Actions'].map(h => (
                      <th key={h} className="text-left px-3 py-1.5 font-normal" style={{ borderBottom: `1px solid ${t.cardBorder}`, fontSize: 10 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {/* Existing members */}
                  {members.map((m, i) => {
                    const st = STATUS_STYLE[m.status] || STATUS_STYLE.deactivated;
                    return (
                      <tr key={m.id} data-testid={`team-member-${m.id}`}>
                        <td className="px-3 py-1.5" style={{ borderBottom: `1px solid ${t.cardBorder}`, color: t.textDim }}>{i + 1} of {r.max_seats}</td>
                        <td className="px-3 py-1.5" style={{ borderBottom: `1px solid ${t.cardBorder}`, color: t.text }}>{m.email}</td>
                        <td className="px-3 py-1.5" style={{ borderBottom: `1px solid ${t.cardBorder}`, color: t.text }}>{m.full_name || <em style={{ color: t.textDim }}>—</em>}</td>
                        <td className="px-3 py-1.5" style={{ borderBottom: `1px solid ${t.cardBorder}` }}>
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold" style={{ background: st.bg, color: st.color }}>
                            {st.label}
                          </span>
                        </td>
                        <td className="px-3 py-1.5" style={{ borderBottom: `1px solid ${t.cardBorder}` }}>
                          <div className="flex items-center gap-1">
                            {state.is_project_admin && m.role !== 'project_admin' && m.status !== 'deactivated' && (
                              <button onClick={() => doResend(m.id)} disabled={busy[m.id]} title="Resend onboarding email + rotate temp password"
                                className="px-1.5 py-0.5 rounded text-[10px] flex items-center gap-0.5"
                                style={{ background: t.inputBg, color: t.textMuted }}
                                data-testid={`team-resend-${m.id}`}>
                                {busy[m.id] ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCw className="w-3 h-3" />} Resend
                              </button>
                            )}
                            {state.is_project_admin && m.role !== 'project_admin' && (
                              <button onClick={() => doPatch(m.id, { status: m.status === 'deactivated' ? 'active' : 'deactivated' })} disabled={busy[m.id]}
                                className="px-1.5 py-0.5 rounded text-[10px] flex items-center gap-0.5"
                                style={{ background: t.inputBg, color: m.status === 'deactivated' ? '#22c55e' : '#ef4444' }}
                                data-testid={`team-toggle-${m.id}`}>
                                <Ban className="w-3 h-3" /> {m.status === 'deactivated' ? 'Reactivate' : 'Deactivate'}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {/* Empty invite rows up to max_seats */}
                  {state.is_project_admin && Array.from({ length: Math.max(0, r.max_seats - members.filter(m => m.status !== 'deactivated').length) }).map((_, idx) => {
                    const key = `${r.key}#${idx}`;
                    const d = drafts[key] || {};
                    const seatNum = members.filter(m => m.status !== 'deactivated').length + idx + 1;
                    return (
                      <tr key={key} data-testid={`team-invite-row-${r.key}-${idx}`}>
                        <td className="px-3 py-1.5" style={{ borderBottom: `1px solid ${t.cardBorder}`, color: t.textDim }}>{seatNum} of {r.max_seats}</td>
                        <td className="px-3 py-1.5" style={{ borderBottom: `1px solid ${t.cardBorder}` }}>
                          <input value={d.email || ''} onChange={e => setDraft(key, { email: e.target.value })}
                            placeholder="name@company.com" type="email"
                            className="w-full rounded px-1.5 py-0.5"
                            style={{ background: t.inputBg, border: `1px solid ${t.inputBorder}`, color: t.text, fontSize: 11 }}
                            data-testid={`team-invite-email-${r.key}-${idx}`} />
                        </td>
                        <td className="px-3 py-1.5" style={{ borderBottom: `1px solid ${t.cardBorder}` }}>
                          <input value={d.full_name || ''} onChange={e => setDraft(key, { full_name: e.target.value })}
                            placeholder="Optional"
                            className="w-full rounded px-1.5 py-0.5"
                            style={{ background: t.inputBg, border: `1px solid ${t.inputBorder}`, color: t.text, fontSize: 11 }} />
                        </td>
                        <td className="px-3 py-1.5" style={{ borderBottom: `1px solid ${t.cardBorder}`, color: t.textDim }}>Not invited</td>
                        <td className="px-3 py-1.5" style={{ borderBottom: `1px solid ${t.cardBorder}` }}>
                          <button onClick={() => doInvite(r.key, key)} disabled={busy[key] || !d.email}
                            className="px-2 py-0.5 rounded text-[11px] font-semibold flex items-center gap-1"
                            style={{ background: (d.email && !busy[key]) ? t.accent : t.inputBg, color: (d.email && !busy[key]) ? '#fff' : t.textDim, opacity: (d.email && !busy[key]) ? 1 : 0.7 }}
                            data-testid={`team-invite-btn-${r.key}-${idx}`}>
                            {busy[key] ? <Loader2 className="w-3 h-3 animate-spin" /> : <UserPlus className="w-3 h-3" />} Invite
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {/* Non-admin: no invite rows, just a note */}
                  {!state.is_project_admin && members.length === 0 && (
                    <tr><td colSpan={5} className="px-3 py-2 text-center italic" style={{ color: t.textDim, borderBottom: `1px solid ${t.cardBorder}` }}>No one in this seat yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          );
        })}
      </div>

      {credsModal && <CredsModal t={t} {...credsModal} onClose={() => setCredsModal(null)} />}
    </div>
  );
}

/* ── Org chart (static SVG-style cards) ── */
const OrgChart = ({ t, roles, fill }) => {
  const tier = (n) => roles.filter(r => r.tier === n);
  const RoleCard = ({ role, narrow }) => (
    <div className={`rounded-lg p-2 ${narrow ? '' : 'flex-1'}`}
      style={{ background: t.card, border: `1px solid ${t.cardBorder}`, minWidth: narrow ? 200 : undefined }}>
      <div className="flex items-baseline gap-2 mb-0.5">
        <Shield className="w-3 h-3 flex-shrink-0" style={{ color: t.accent }} />
        <div className="text-[11px] font-bold flex-1 truncate" style={{ color: t.text }}>{role.label}</div>
        <span className="text-[9px] font-semibold px-1 py-0 rounded"
          style={{ background: (fill[role.key] || 0) >= role.min_seats ? 'rgba(34,197,94,0.15)' : 'rgba(234,179,8,0.15)', color: (fill[role.key] || 0) >= role.min_seats ? '#22c55e' : '#eab308' }}>
          {fill[role.key] || 0} / {role.max_seats}
        </span>
      </div>
      <div className="text-[10px] leading-tight" style={{ color: t.textDim }}>{role.summary}</div>
    </div>
  );
  return (
    <div className="mb-4 p-3 rounded-lg" style={{ background: t.card, border: `1px dashed ${t.cardBorder}` }} data-testid="team-org-chart">
      <div className="text-[10px] font-bold uppercase tracking-[0.1em] mb-2" style={{ color: t.textDim }}>Materials governance org chart</div>
      <div className="mb-2 text-center">
        <div className="inline-block px-3 py-1 rounded" style={{ background: 'rgba(99,102,241,0.15)', color: '#818cf8', fontSize: 10, fontWeight: 700 }}>
          Platform Creator · Durable Designs
        </div>
      </div>
      <div className="flex justify-center mb-2">
        {tier(1).map(r => <RoleCard key={r.key} role={r} narrow />)}
      </div>
      <div className="flex flex-wrap gap-2 mb-2">
        {tier(2).map(r => <RoleCard key={r.key} role={r} />)}
        {tier(3).map(r => <RoleCard key={r.key} role={r} />)}
      </div>
      <div className="flex flex-wrap gap-2">
        {tier(4).map(r => <RoleCard key={r.key} role={r} />)}
      </div>
    </div>
  );
};

/* ── Credentials modal ── */
const CredsModal = ({ t, email, temp_password, email_delivery, role_label, onClose }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(`Username: ${email}\nTemporary password: ${temp_password}`); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* ignore */ }
  };
  const emailOk = email_delivery?.status === 'sent';
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.55)' }} data-testid="team-creds-modal">
      <div className="rounded-lg w-full max-w-md" style={{ background: t.sidebar, border: `1px solid ${t.cardBorder}`, color: t.text }}>
        <div className="px-4 py-3 border-b flex items-center gap-2" style={{ borderColor: t.cardBorder }}>
          <CheckCircle2 className="w-4 h-4" style={{ color: '#22c55e' }} />
          <div className="flex-1">
            <div className="text-sm font-bold">Invite sent</div>
            <div className="text-[11px]" style={{ color: t.textMuted }}>{role_label} · {email}</div>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/5" data-testid="team-creds-modal-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-4 space-y-3 text-[11px]">
          <div className="p-3 rounded flex flex-col gap-1" style={{ background: t.inputBg, border: `1px solid ${t.inputBorder}` }}>
            <div><span style={{ color: t.textDim }}>Username:</span> <strong>{email}</strong></div>
            <div className="flex items-center gap-2 flex-wrap">
              <span style={{ color: t.textDim }}>Temporary password:</span>
              <code style={{ background: t.card, padding: '2px 6px', borderRadius: 4 }}>{temp_password}</code>
              <button onClick={copy} className="px-1.5 py-0.5 rounded text-[10px] font-semibold flex items-center gap-0.5"
                style={{ background: t.accent, color: '#fff' }} data-testid="team-creds-copy">
                <Copy className="w-3 h-3" /> {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
          </div>
          <div className="p-2 rounded text-[11px] flex items-start gap-2"
            style={{ background: emailOk ? 'rgba(34,197,94,0.08)' : 'rgba(234,179,8,0.08)', border: `1px solid ${emailOk ? 'rgba(34,197,94,0.35)' : 'rgba(234,179,8,0.35)'}` }}>
            <Mail className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: emailOk ? '#22c55e' : '#eab308' }} />
            <div style={{ color: t.text }}>
              {emailOk ? (
                <>Onboarding email sent to <strong>{email}</strong>. The user will be forced to set a new password on first login.</>
              ) : email_delivery?.status === 'skipped_no_key' ? (
                <>Resend not configured. Copy the credentials above and share them directly. <strong>Must change password on first login.</strong></>
              ) : (
                <>Email delivery failed ({email_delivery?.detail || 'unknown'}). Credentials above are valid — share them manually. <strong>Must change password on first login.</strong></>
              )}
            </div>
          </div>
        </div>
        <div className="px-4 py-3 border-t flex justify-end" style={{ borderColor: t.cardBorder }}>
          <button onClick={onClose} className="px-3 py-1.5 rounded text-[11px] font-semibold"
            style={{ background: t.accent, color: '#fff' }}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

/* ═════════════════════════════════════════════════════════════════
   PriorisFirstLoginGate — blocks access until the user has set a new
   password (for invited users with must_change_password=true). Shows
   the role welcome screen once the password is accepted.
   ═════════════════════════════════════════════════════════════════ */
export function PriorisFirstLoginGate({ t, user, onComplete }) {
  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [memberships, setMemberships] = useState([]);
  const [stage, setStage] = useState('change');  // 'change' | 'welcome'

  useEffect(() => {
    fetchSln(`${RBAC}/me`).then(r => setMemberships(r.memberships || [])).catch(() => setMemberships([]));
  }, []);

  const policy = {
    len: pw1.length >= 12,
    upper: /[A-Z]/.test(pw1),
    digit: /\d/.test(pw1),
    sym: /[^A-Za-z0-9]/.test(pw1),
    match: pw1.length > 0 && pw1 === pw2,
  };
  const ok = Object.values(policy).every(Boolean);

  const submit = async () => {
    if (!ok) return;
    setSaving(true); setError(null);
    try {
      await fetchSln(`${RBAC}/activate`, { method: 'POST', body: JSON.stringify({ new_password: pw1 }) });
      setStage('welcome');
    } catch (e) { setError(e.message); }
    setSaving(false);
  };

  const PolicyItem = ({ ok, label }) => (
    <div className="flex items-center gap-1 text-[11px]" style={{ color: ok ? '#22c55e' : t.textDim }}>
      {ok ? <CheckCircle2 className="w-3 h-3" /> : <span className="w-3 h-3 inline-block rounded-full border" style={{ borderColor: t.textDim }} />}
      {label}
    </div>
  );

  if (stage === 'welcome') {
    return <RoleWelcome t={t} user={user} memberships={memberships} onContinue={onComplete} />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: t.bg, color: t.text }} data-testid="first-login-gate">
      <div className="w-full max-w-md rounded-lg" style={{ background: t.card, border: `1px solid ${t.cardBorder}` }}>
        <div className="px-5 py-4 border-b flex items-center gap-2" style={{ borderColor: t.cardBorder }}>
          <Lock className="w-4 h-4" style={{ color: t.accent }} />
          <div>
            <div className="text-sm font-bold">Set a new password</div>
            <div className="text-[11px]" style={{ color: t.textMuted }}>Required before you can continue</div>
          </div>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <div className="text-[10px] font-semibold mb-0.5" style={{ color: t.textDim }}>New password</div>
            <input type="password" value={pw1} onChange={e => setPw1(e.target.value)} autoFocus
              className="w-full rounded px-2 py-1.5"
              style={{ background: t.inputBg, border: `1px solid ${t.inputBorder}`, color: t.text, fontSize: 12 }}
              data-testid="first-login-pw1" />
          </div>
          <div>
            <div className="text-[10px] font-semibold mb-0.5" style={{ color: t.textDim }}>Confirm password</div>
            <input type="password" value={pw2} onChange={e => setPw2(e.target.value)}
              className="w-full rounded px-2 py-1.5"
              style={{ background: t.inputBg, border: `1px solid ${t.inputBorder}`, color: t.text, fontSize: 12 }}
              data-testid="first-login-pw2" />
          </div>
          <div className="p-2 rounded space-y-0.5" style={{ background: t.inputBg }}>
            <PolicyItem ok={policy.len}   label="At least 12 characters" />
            <PolicyItem ok={policy.upper} label="One uppercase letter (A–Z)" />
            <PolicyItem ok={policy.digit} label="One digit (0–9)" />
            <PolicyItem ok={policy.sym}   label="One symbol (!@#$…)" />
            <PolicyItem ok={policy.match} label="Passwords match" />
          </div>
          {error && <div className="text-[11px] px-2 py-1 rounded" style={{ background: 'rgba(239,68,68,0.12)', color: '#ef4444' }}>{error}</div>}
          <button onClick={submit} disabled={!ok || saving}
            className="w-full py-2 rounded font-semibold text-[12px] flex items-center justify-center gap-1.5 transition"
            style={{ background: ok ? t.accent : t.inputBg, color: ok ? '#fff' : t.textDim, opacity: saving ? 0.7 : 1 }}
            data-testid="first-login-submit">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
            Save password and continue
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Role welcome screen shown right after first-login password change ── */
const RoleWelcome = ({ t, user, memberships, onContinue }) => {
  const roleLabels = {
    project_admin: 'Project Admin',
    mat_engineer: "Contractor's Materials Engineer",
    mat_technician: "Contractor's Materials Technician",
    engineer_of_record: 'Engineer of Record',
    resident_engineer: 'Resident Engineer',
    consultant: 'Materials / Testing Consultant',
    asset_owner: 'Asset Owner',
  };
  const roleBullets = {
    project_admin: ['Create & edit projects', 'Apply normative standards', 'Invite all other roles', 'Oversee everything'],
    mat_engineer: ['Submit RAMs', 'Sign off QC records to EoR', 'Append test results'],
    mat_technician: ['Fill ITPs', 'Enter pour records', 'Add observations'],
    engineer_of_record: ['Approve / reject submittals', 'View all QC & SL outputs'],
    resident_engineer: ['Ultimate rejection authority', 'Override EoR with audited comment', 'Approve submittals'],
    consultant: ['Advisory comments on MA & QC', 'Read-only across MA & QC'],
    asset_owner: ['Client oversight', 'View-only summary across modules'],
  };
  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: t.bg, color: t.text }} data-testid="role-welcome">
      <div className="w-full max-w-lg rounded-lg" style={{ background: t.card, border: `1px solid ${t.cardBorder}` }}>
        <div className="px-5 py-6 text-center border-b" style={{ borderColor: t.cardBorder }}>
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg mb-3" style={{ background: 'rgba(16,185,129,0.15)' }}>
            <Shield className="w-6 h-6" style={{ color: t.accent }} />
          </div>
          <div className="text-[11px] uppercase tracking-wider font-semibold" style={{ color: t.accent }}>Welcome to Prioris</div>
          <h1 className="text-base font-bold mt-0.5">{user?.name || user?.email}</h1>
        </div>
        <div className="p-5">
          {memberships.length === 0 ? (
            <p className="text-[11px] text-center" style={{ color: t.textDim }}>You have no project assignments yet. Ask your Project Admin to invite you.</p>
          ) : (
            <div className="space-y-3">
              {memberships.map(m => (
                <div key={m.id} className="p-3 rounded" style={{ background: t.inputBg, border: `1px solid ${t.inputBorder}` }}>
                  <div className="text-[11px] font-bold" style={{ color: t.text }}>{roleLabels[m.role] || m.role}</div>
                  <div className="text-[10px]" style={{ color: t.textDim }}>Project: {m.project_id}</div>
                  <ul className="mt-2 space-y-0.5">
                    {(roleBullets[m.role] || []).map((b, i) => (
                      <li key={i} className="flex items-center gap-1 text-[11px]" style={{ color: t.text }}>
                        <ChevronRight className="w-2.5 h-2.5" style={{ color: t.accent }} /> {b}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="px-5 pb-5">
          <button onClick={onContinue} className="w-full py-2 rounded font-semibold text-[12px]"
            style={{ background: t.accent, color: '#fff' }}
            data-testid="role-welcome-continue">
            Go to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};
